import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { RoleSelect } from './RoleSelect';
import { UserRole } from '../lib/api';
import { Building2 } from 'lucide-react';

export const AuthForm: React.FC = () => {
  const { signUp, signIn } = useAuth();
  const [step, setStep] = useState<'auth' | 'role'>('auth');
  const [isSignUp, setIsSignUp] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
  });

  const handleRoleSelect = async (role: UserRole) => {
    setSelectedRole(role);
    setLoading(true);
    setError('');

    try {
      await signUp(formData.email, formData.password, role, formData.fullName || '');
    } catch (err: any) {
      setError(err.message || 'An error occurred');
      setStep('auth');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (isSignUp) {
      setStep('role');
      return;
    }

    setLoading(true);

    try {
      await signIn(formData.email, formData.password);
    } catch (err: any) {
      setError(err.message || 'An error occurred');
      setLoading(false);
    }
  };

  if (step === 'role') {
    return <RoleSelect onRoleSelect={handleRoleSelect} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:20px_20px]"></div>
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-[linear-gradient(30deg,transparent_0%,rgba(251,146,60,0.15)_50%,transparent_100%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(150deg,transparent_0%,rgba(253,186,116,0.12)_50%,transparent_100%)]"></div>
      </div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-400/10 rounded-full blur-3xl"></div>

      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-amber-600 flex items-center justify-center shadow-2xl">
              <Building2 className="w-9 h-9 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">VendorBoom</h1>
          <p className="text-slate-300">Connect contractors with opportunities</p>
        </div>

        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 border border-white/20">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">
              {isSignUp ? 'Create Account' : 'Welcome Back'}
            </h2>
            <p className="text-slate-600 text-sm">
              {isSignUp ? 'Get started with VendorBoom' : 'Sign in to your account'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {isSignUp && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 transition-all outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 hover:border-slate-300 hover:shadow-md"
                  placeholder="John Doe"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Email
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 transition-all outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 hover:border-slate-300 hover:shadow-md"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Password
              </label>
              <input
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border-2 border-slate-200 transition-all outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 hover:border-slate-300 hover:shadow-md"
                placeholder="••••••••"
                minLength={8}
              />
              {isSignUp && (
                <p className="mt-2 text-xs text-slate-500">
                  Password must be at least 8 characters long
                </p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm font-medium">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full text-white font-semibold py-3.5 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl hover:scale-[1.02]"
            >
              {loading
                ? 'Please wait...'
                : isSignUp
                  ? 'Continue'
                  : 'Sign In'
              }
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsSignUp(!isSignUp);
                setStep('auth');
                setSelectedRole(null);
                setError('');
              }}
              className="text-sm text-slate-600 hover:text-blue-600 transition-colors font-medium"
            >
              {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
